import React, { useState, useMemo, useCallback } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';

import config from '../../config';
import { FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import { AiFillEuroCircle } from 'react-icons/ai';
import { IoLanguage } from 'react-icons/io5';
import { IoShareOutline } from 'react-icons/io5';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import { BiSolidQuoteAltRight } from 'react-icons/bi';

import { Modal, CodeOfConductModalContent, Tooltip, AvatarLarge } from '../../components';

import css from './ProfileUserCard.css';

// Constants
const TOAST_DURATION = 2500;

// Custom hook for profile data
const useProfileData = profileUser => {
  return useMemo(() => {
    const profile = profileUser?.attributes?.profile;
    const publicData = profile?.publicData || {};

    return {
      displayName: profile?.displayName || '',
      whoAmI: publicData.whoAmI || '',
      location: publicData.location || '',
      languages: publicData.languages || [],
      hourlyFee: publicData.hourlyFee || '',
      dontShowHourlyFee: publicData.dontShowHourlyFee || false,
      profileUserUuid: profileUser?.id?.uuid || '',
      cocAccepted: publicData.cocAccepted || '',
    };
  }, [profileUser]);
};

// Custom hook for testimonials
const usePublishedTestimonials = offPlatformTestimonials => {
  return useMemo(() => {
    if (!offPlatformTestimonials || offPlatformTestimonials.length === 0) return [];
    return offPlatformTestimonials.filter(t => t.state === 'published');
  }, [offPlatformTestimonials]);
};

// Custom hook for reviews data
const useReviewsData = combinedReviews => {
  return useMemo(() => {
    if (!combinedReviews?.length) return null;

    const averageRating = (
      combinedReviews.reduce((a, b) => a + b?.rating, 0) / combinedReviews.length
    ).toFixed(1);

    return {
      averageRating,
      reviewCount: combinedReviews.length,
    };
  }, [combinedReviews]);
};

// Custom hook for billed total
const useBilledTier = profileUser => {
  return useMemo(() => {
    const billedTotal = profileUser?.attributes?.profile?.metadata?.billedTotal;
    if (billedTotal == null || billedTotal <= 0) return null;

    const tiers = [100000, 50000, 25000, 10000, 5000, 1000];
    const tier = tiers.find(t => billedTotal >= t);

    if (!tier) return null;

    // Format the tier value
    if (tier >= 1000) {
      return `€${tier / 1000}K+`;
    }
    return `€${tier}+`;
  }, [profileUser]);
};

// Custom hook for languages
const useLanguagesData = languages => {
  const languageOptions = config.custom.filters[3].config.options;

  return useMemo(() => {
    if (!languages?.length) return null;

    const keys = languages.map(l => (typeof l === 'string' ? l : l?.language)).filter(Boolean);

    if (!keys.length) return null;

    return keys.map((key, index, arr) => (
      <span key={key}>
        {languageOptions.find(l => l.key === key)?.label || key}
        {index < arr.length - 1 ? ', ' : ''}
      </span>
    ));
  }, [languages, languageOptions]);
};

// Custom hook for translation messages
const useTranslationMessages = intl => {
  return useMemo(
    () => ({
      cocHeading: intl.formatMessage({ id: 'ProfileSettingsForm.cocHeading' }),
      cocText: intl.formatMessage({ id: 'ProfileSettingsForm.cocText' }),
      openCoc: intl.formatMessage({ id: 'ProfileSettingsForm.cocOpenCoc' }),
      shareSuccess: intl.formatMessage({ id: 'FreelancerProfileUserCard.shareSuccess' }),
      shareError: intl.formatMessage({ id: 'FreelancerProfileUserCard.shareError' }),
    }),
    [intl]
  );
};

// Toast component
const Toast = ({ message }) => {
  if (!message) return null;
  return <div className={css.toast}>{message}</div>;
};

// Profile image component
const ProfileImageSection = ({ profileUser }) => (
  <div className={css.profileImageContainer}>
    <AvatarLarge user={profileUser} className={css.profileImage} />
  </div>
);

// Reviews component
const ReviewsSection = ({ reviewsData }) => {
  if (!reviewsData) return null;

  return (
    <Tooltip messageId="Tooltip.reviews">
      <div className={css.reviews}>
        <div className={css.reviewsTop}>
          <FaStar className={css.starIcon} />
          <span className={css.reviewsRating}>{reviewsData.averageRating}</span>
          <span className={css.reviewsCount}>({reviewsData.reviewCount})</span>
        </div>
        <div className={css.reviewsLabel}>
          <FormattedMessage id="FreelancerProfileUserCard.reviews" />
        </div>
      </div>
    </Tooltip>
  );
};

// Testimonials component
const TestimonialsSection = ({ publishedTestimonials }) => {
  if (!publishedTestimonials?.length) return null;

  return (
    <Tooltip messageId="Tooltip.testimonials">
      <div className={css.testimonials}>
        <div className={css.testimonialsTop}>
          <BiSolidQuoteAltRight className={css.quoteIcon} />
          <span className={css.testimonialsCount}>{publishedTestimonials.length}</span>
        </div>
        <div className={css.testimonialsLabel}>
          <FormattedMessage id="FreelancerProfileUserCard.testimonials" />
        </div>
      </div>
    </Tooltip>
  );
};

// Billed section component
const BilledSection = ({ billedTier }) => {
  if (!billedTier) return null;

  return (
    <Tooltip messageId="Tooltip.earnings">
      <div className={css.billedSection}>
        <div className={css.billedAmount}>{billedTier}</div>
        <div className={css.billedLabel}>
          <FormattedMessage id="FreelancerProfileUserCard.earnings" />
        </div>
      </div>
    </Tooltip>
  );
};

// Who am I section component
const WhoAmISection = ({
  displayName,
  whoAmI,
  reviewsData,
  publishedTestimonials,
  billedTier,
  contactButton,
}) => {
  const hasAnyData =
    reviewsData || billedTier || (publishedTestimonials && publishedTestimonials.length > 0);

  return (
    <div className={css.whoAmIContainer}>
      <div className={css.displayName}>{displayName}</div>
      <div className={css.whoAmI}>{whoAmI}</div>
      {contactButton}
      {hasAnyData && (
        <div className={css.reviewAndTestimonials}>
          <ReviewsSection reviewsData={reviewsData} />
          <BilledSection billedTier={billedTier} />
          <TestimonialsSection publishedTestimonials={publishedTestimonials} />
        </div>
      )}
    </div>
  );
};

// Location section component
const LocationSection = ({ location }) => {
  if (!location || location === '' || location?.selectedPlace === null) return null;

  return (
    <div className={css.locationSection}>
      <FaMapMarkerAlt className={css.mapMarkerIcon} />
      {location?.search.split(',')[0]}
    </div>
  );
};

// Languages section component
const LanguagesSection = ({ languagesString }) => {
  if (!languagesString) return null;

  return (
    <div className={css.languageSection}>
      <IoLanguage className={css.languageIcon} />
      <div className={css.languageContainer}>{languagesString}</div>
    </div>
  );
};

// Hourly fee section component
const HourlyFeeSection = ({ hourlyFee, dontShowHourlyFee }) => {
  if (dontShowHourlyFee || !hourlyFee) return null;

  return (
    <div className={css.hourlyFeeSection}>
      <AiFillEuroCircle className={css.euroIcon} />
      {hourlyFee}
    </div>
  );
};

// CoC section component
const CoCSection = ({ cocAccepted, messages, button, onOpenCocModal }) => (
  <div className={css.cocSection}>
    <div className={css.cocSectionTitle}>{messages.cocHeading}</div>
    <div className={css.cocContainer}>
      <BsFillCheckCircleFill className={cocAccepted ? css.cocIcon : css.cocIconNotAccepted} />
      <div className={css.cocText}>{messages.cocText}</div>
    </div>
    {!button && (
      <div className={css.openCoc} onClick={onOpenCocModal}>
        {messages.openCoc}
      </div>
    )}
  </div>
);

// CoC Modal component
const CoCModal = ({ isOpen, onClose }) => (
  <Modal
    id="ProfileSettingsForm.coc"
    isOpen={isOpen}
    onClose={onClose}
    onManageDisableScrolling={manageDisableScrolling}
    usePortal
  >
    <CodeOfConductModalContent />
  </Modal>
);

const FreelancerProfileUserCardComponent = ({
  profileUser,
  combinedReviews,
  offPlatformTestimonials,
  button,
  intl,
  contactButton,
}) => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const profileData = useProfileData(profileUser);
  const publishedTestimonials = usePublishedTestimonials(offPlatformTestimonials);
  const reviewsData = useReviewsData(combinedReviews);
  const billedTier = useBilledTier(profileUser);
  const languagesString = useLanguagesData(profileData.languages);
  const messages = useTranslationMessages(intl);

  const handleOpenCocModal = useCallback(() => setTosModalOpen(true), []);
  const handleCloseCocModal = useCallback(() => setTosModalOpen(false), []);

  return (
    <div className={css.profileUserCard}>
      <Toast message={toastMessage} />
      <ProfileImageSection profileUser={profileUser} />
      <WhoAmISection
        displayName={profileData.displayName}
        whoAmI={profileData.whoAmI}
        reviewsData={reviewsData}
        publishedTestimonials={publishedTestimonials}
        billedTier={billedTier}
        contactButton={contactButton}
      />
      <LocationSection location={profileData.location} />
      <LanguagesSection languagesString={languagesString} />
      <HourlyFeeSection
        hourlyFee={profileData.hourlyFee}
        dontShowHourlyFee={profileData.dontShowHourlyFee}
      />
      <CoCSection
        cocAccepted={profileData.cocAccepted}
        messages={messages}
        button={button}
        onOpenCocModal={handleOpenCocModal}
      />
      <CoCModal isOpen={tosModalOpen} onClose={handleCloseCocModal} />
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const FreelancerProfileUserCard = compose(
  connect(mapDispatchToProps),
  injectIntl
)(FreelancerProfileUserCardComponent);

export default FreelancerProfileUserCard;
